import {Subject} from "rxjs";
import {distinctUntilChanged, map, switchMap} from "rxjs/operators";
import {CompleterData, CompleterItem} from "ng2-completer/public_api";
import {
  GbgrundstueckService,
  GrundstueckOneFieldSearchListEntry,
  GrundstueckOneFieldSearchRequest,
  GrundstueckOneFieldSearchResponse
} from "app/core/gb-api";
import {GrundstuecknummerPipe} from "app/shared/pipes/simple/grundstuecknummer.pipe";
import {KompaktGebaeudeeingangsadressePipe} from "app/shared/kompaktdarstellung/gebaeudeeingangsadresse/kompakt-gebaeudeeingangsadresse-pipe";

const GEBAEUDE_FIELD = 'gebaeude';
const GEBAEUDEEINGANGADRESSE_FIELD = 'gebaeudeeingangAdresse';
const FLURNAME_FIELD = 'flurname';

export class SearchbarCompleterData extends Subject<CompleterItem[]> implements CompleterData {

  private search$ = new Subject<string>();

  constructor(private grundstueckService: GbgrundstueckService,
              private grundstuecknummerPipe: GrundstuecknummerPipe,
              private gebaedeAdressePipe: KompaktGebaeudeeingangsadressePipe
  ) {
    super();
    this.init();
  }

  private init() {
    this.search$.pipe(
      distinctUntilChanged(),
      switchMap(term => {
        return this.grundstueckService.searchGrundstueckOneField(this.createRequestObject(term));
      }),
      map((data: GrundstueckOneFieldSearchResponse) => {
        const matches = data.results
          .map((searchResult: GrundstueckOneFieldSearchListEntry) => this.convertToItem(searchResult));
        this.next(matches);
      }))
      .subscribe();
  }

  public search(term: string): void {
    this.search$.next(term);
  }

  private createRequestObject(searchQuery: string): GrundstueckOneFieldSearchRequest {
    return new class implements GrundstueckOneFieldSearchRequest {
      searchQuery = searchQuery;
    };
  }

  public cancel() {
  }

  public convertToItem(data: GrundstueckOneFieldSearchListEntry): CompleterItem | null {
    if (!data) {
      return null;
    }
    return {
      title: this.formatEntry(data),
      originalObject: data
    } as CompleterItem;
  }

  private formatEntry(entry: GrundstueckOneFieldSearchListEntry): string {
    let grundstueckNummer = this.grundstuecknummerPipe.transform(entry.grundstueckNummer);
    if (entry.grundstueckNummer.egrid) {
      grundstueckNummer += '; ' + entry.grundstueckNummer.egrid.value;
    }
    const displayAdressOrFlurname = this.getHighestScoreNestedElement(entry) || this.getFirstAddress(entry) || this.getFirstFlurname(entry);
    return [grundstueckNummer, displayAdressOrFlurname].filter(Boolean).join('; ');
  }

  private getHighestScoreNestedElement(entry: GrundstueckOneFieldSearchListEntry): string {

    const gebaeude = entry.match.nestedMatchFieldOffset.find(a => a.nestedFieldName === GEBAEUDE_FIELD);
    const gebaeudeeingangAdresse = entry.match.nestedMatchFieldOffset.find(a => a.nestedFieldName === GEBAEUDEEINGANGADRESSE_FIELD);
    const flurname = entry.match.nestedMatchFieldOffset.find(a => a.nestedFieldName === FLURNAME_FIELD);

    const gebaeudeeingangAdresseOffset = gebaeudeeingangAdresse ? gebaeudeeingangAdresse.nestedFieldMatchOffset : 0;

    if (flurname && gebaeude) {
      if (flurname.score > gebaeude.score) {
        return this.getNestedFlurname(entry, flurname.nestedFieldMatchOffset);
      }
      return this.gebaedeAdressePipe.transform(this.getNestedAdress(entry, gebaeude.nestedFieldMatchOffset, gebaeudeeingangAdresseOffset));
    }

    if (gebaeude) {
      return this.gebaedeAdressePipe.transform(this.getNestedAdress(entry, gebaeude.nestedFieldMatchOffset, gebaeudeeingangAdresseOffset));
    }

    if (flurname) {
      return this.getNestedFlurname(entry, flurname.nestedFieldMatchOffset);
    }
  }

  private getFirstFlurname(entry: GrundstueckOneFieldSearchListEntry) {
    return this.getNestedFlurname(entry, 0);
  }

  private getFirstAddress(entry: GrundstueckOneFieldSearchListEntry) {
    const address = this.getNestedAdress(entry, 0, 0);
    if (address) {
      return this.gebaedeAdressePipe.transform(address);
    }
    return undefined;
  }


  private getNestedFlurname(entry: GrundstueckOneFieldSearchListEntry, offset: number): string {
    if (entry.flurnamen && entry.flurnamen[offset]) {
      return entry.flurnamen[offset];
    }
    return undefined;
  }

  private getNestedAdress(entry: GrundstueckOneFieldSearchListEntry, offsetGebaeude: number, offsetEingangAdresse: number) {
    if (entry.gebauede[offsetGebaeude] && entry.gebauede[offsetGebaeude].eingangAdresse &&
      entry.gebauede[offsetGebaeude].eingangAdresse[offsetEingangAdresse]) {
      return entry.gebauede[offsetGebaeude].eingangAdresse[offsetEingangAdresse];
    }
    return undefined;
  }
}
